/**
 * Chat-panel window events (M25.2 / M25.4).
 *
 * Decoupled triggers for the chat panel — a window CustomEvent (not
 * a React context) lets any component anywhere in the tree drive
 * the panel without a provider. Two events:
 *
 *   - `CHAT_EXPLAIN_EVENT` — an `<Explainable />` affordance records
 *     the clicked element as the chat's focused element (M25.1) and
 *     asks the chat to explain it. AppShell opens the panel;
 *     ChatPanel sends the "explain this" message.
 *   - `CHAT_OPEN_THREAD_EVENT` — the /ai/threads page opens a pinned
 *     thread. AppShell opens the panel; ChatPanel loads the thread.
 */

import {
  type FocusedElement,
  setChatFocusedElement,
} from "./chat-context-rich";

export const CHAT_EXPLAIN_EVENT = "solvait:chat-explain";
export const CHAT_OPEN_THREAD_EVENT = "solvait:chat-open-thread";
export const CHAT_OPEN_EVENT = "solvait:chat-open";

export interface ChatExplainDetail {
  prompt: string;
  element: FocusedElement;
}

export interface ChatOpenThreadDetail {
  threadId: string;
}

/**
 * Record `element` as the chat's focused element and ask the chat
 * to explain it. Opens the panel + sends the message via the two
 * window-event listeners.
 */
export function requestChatExplain(element: FocusedElement): void {
  setChatFocusedElement(element);
  const prompt = `Explain this: "${element.label}".`;
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<ChatExplainDetail>(CHAT_EXPLAIN_EVENT, {
        detail: { prompt, element },
      }),
    );
  }
}

/** Open a pinned thread in the chat panel (from /ai/threads). */
export function requestOpenChatThread(threadId: string): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<ChatOpenThreadDetail>(CHAT_OPEN_THREAD_EVENT, {
        detail: { threadId },
      }),
    );
  }
}

/**
 * Just open the chat panel. No auto-message. Used when we want
 * to give HR the chat surface but let them type their own
 * question — e.g. when an Import session lands in
 * awaiting_clarification and the user needs to chat without
 * spending tokens on a pre-canned message.
 *
 * Optionally accepts `prefillInput` — text dropped into the
 * chat input box for HR to review + send manually. NOT sent
 * automatically so HR can edit before incurring AI token cost.
 */
export interface ChatOpenDetail {
  prefillInput?: string;
}

export const CHAT_PREFILL_KEY = "solvait-aihr.chat-prefill";

export function openChatPanel(detail?: ChatOpenDetail): void {
  if (typeof window === "undefined") return;
  if (detail?.prefillInput) {
    // Stash in sessionStorage — ChatPanel reads + clears on open.
    try {
      sessionStorage.setItem(CHAT_PREFILL_KEY, detail.prefillInput);
    } catch {
      // sessionStorage unavailable — non-fatal; the panel just
      // opens without the prefill.
    }
  }
  window.dispatchEvent(new CustomEvent<ChatOpenDetail>(CHAT_OPEN_EVENT, {
    detail: detail ?? {},
  }));
}
